import gsap from "gsap";
import { ONBOARDING_WELCOME_ANIM } from "./animationConstants";
import { supportsOnboardingAccentColorMix } from "./onboardingWelcomeAccentSupport";

const ACCENT_PHRASE_SELECTOR = ".onboarding-welcome-accent-phrase";

export type OnboardingWelcomeGsapTimeline = gsap.core.Timeline;

export interface OnboardingWelcomeEntranceTargets {
  waveChunks: HTMLElement[];
  numberRollers: HTMLElement[];
  accentPhrases: HTMLElement[];
  mainAction: HTMLButtonElement | null;
}

export const queryOnboardingWelcomeTargets = (
  root: HTMLElement,
  cta: HTMLButtonElement | null,
): OnboardingWelcomeEntranceTargets => ({
  waveChunks: gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll("[data-onboarding-wave-chunk]"),
  ),
  numberRollers: gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(".number-roller"),
  ),
  accentPhrases: gsap.utils.toArray<HTMLElement>(
    root.querySelectorAll(ACCENT_PHRASE_SELECTOR),
  ),
  mainAction: cta,
});

const resetAccentPhrase = (el: HTMLElement) => {
  el.classList.remove("is-accent-animating", "is-accent-final");
  el.style.removeProperty("--onboarding-accent-mix");
};

const playAccentKeyframe = (el: HTMLElement) => {
  resetAccentPhrase(el);
  if (!supportsOnboardingAccentColorMix()) {
    finalizeAccentPhrase(el);
    return;
  }
  void el.offsetWidth;
  el.classList.add("is-accent-animating");
  window.setTimeout(() => {
    if (el.classList.contains("is-accent-animating")) {
      finalizeAccentPhrase(el);
    }
  }, ONBOARDING_WELCOME_ANIM.accentFadeDurationMs + 50);
};

const finalizeAccentPhrase = (el: HTMLElement) => {
  el.classList.remove("is-accent-animating");
  el.classList.add("is-accent-final");
};

const resetEntrance = (targets: OnboardingWelcomeEntranceTargets) => {
  const { waveChunks, numberRollers, accentPhrases, mainAction } = targets;

  gsap.killTweensOf(
    [...waveChunks, mainAction].filter(Boolean) as gsap.TweenTarget[],
  );

  gsap.set(waveChunks, {
    opacity: 0,
    y: ONBOARDING_WELCOME_ANIM.slideY,
  });
  gsap.set(mainAction, {
    opacity: 0,
    y: ONBOARDING_WELCOME_ANIM.slideY,
  });
  accentPhrases.forEach(resetAccentPhrase);
  numberRollers.forEach((el) => {
    el.textContent = "0";
  });
};

const applyReducedMotionFinal = (
  targets: OnboardingWelcomeEntranceTargets,
  targetCount: number,
) => {
  const { waveChunks, numberRollers, accentPhrases, mainAction } = targets;

  gsap.set(waveChunks, { opacity: 1, y: 0 });
  gsap.set(mainAction, { opacity: 1, y: 0 });
  accentPhrases.forEach(finalizeAccentPhrase);
  numberRollers.forEach((el) => {
    el.textContent = String(targetCount);
  });
};

/**
 * GSAP masterTimeline（wave-chunk → 数字 → CSS keyframe 纯色 accent → CTA）
 */
export const runOnboardingWelcomeEntrance = (
  root: HTMLElement,
  cta: HTMLButtonElement | null,
  targetCount: number,
): gsap.core.Timeline => {
  const targets = queryOnboardingWelcomeTargets(root, cta);
  resetEntrance(targets);

  if (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    applyReducedMotionFinal(targets, targetCount);
    return gsap.timeline();
  }

  const durationMs = ONBOARDING_WELCOME_ANIM.textDurationMs;
  const staggerMs = ONBOARDING_WELCOME_ANIM.staggerMs;

  const { waveChunks, numberRollers, accentPhrases, mainAction } = targets;

  const masterTimeline = gsap.timeline({
    defaults: { ease: "power1.out" },
  });

  masterTimeline.to(waveChunks, {
    opacity: 1,
    y: 0,
    stagger: staggerMs / 1000,
    duration: durationMs / 1000,
    ease: "power2.out",
    force3D: true,
    overwrite: "auto",
  });

  const numObj = { value: 0 };
  masterTimeline.to(
    numObj,
    {
      value: targetCount,
      duration: ONBOARDING_WELCOME_ANIM.counterDurationMs / 1000,
      ease: "power2.out",
      onUpdate: () => {
        const currentVal = Math.floor(numObj.value);
        numberRollers.forEach((el) => {
          el.textContent = String(currentVal);
        });
      },
    },
    `-=${(durationMs / 1000) * 1.2}`,
  );

  masterTimeline.call(
    () => {
      accentPhrases.forEach(playAccentKeyframe);
    },
    undefined,
    "-=0.35",
  );

  if (mainAction) {
    masterTimeline.to(
      mainAction,
      {
        opacity: 1,
        y: 0,
        duration: ONBOARDING_WELCOME_ANIM.buttonDurationMs / 1000,
        ease: "power2.out",
        force3D: true,
        overwrite: "auto",
      },
      "-=0.25",
    );
  }

  return masterTimeline;
};
